import { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { LogoutConfirmModal } from '@/components/ui/logout-confirm-modal';
import { isNavPathLocked } from '@/constants/module-locks';
import { ROLES } from '@sime/shared';
import {
  LayoutDashboard,
  FileText,
  HardHat,
  BarChart3,
  Settings,
  ChevronDown,
  Database,
  CalendarDays,
  Upload,
  PackageCheck,
  ClipboardList,
  ListChecks,
  ListTodo,
  X,
  Download,
  Wrench,
  AlertTriangle,
  Building2,
  Users,
  UserCog,
  UserPlus,
  FileBarChart,
  Clock,
  ShieldAlert,
  FileWarning,
  FilePlus2,
  MessageSquare,
  FolderOpen,
  LogOut,
  User,
  type LucideIcon,
} from 'lucide-react';

/* ─── Types ─── */

interface NavLeafItem {
  type: 'leaf';
  path: string;
  label: string;
  icon: LucideIcon;
  roles?: number[];
}

interface NavSectionItem {
  type: 'section';
  id: string;
  label: string;
  icon: LucideIcon;
  roles?: number[];
  children: NavLeafItem[];
}

type NavEntry = NavLeafItem | NavSectionItem;

/* ─── Navigation definition ─── */

const navEntries: NavEntry[] = [
  { type: 'leaf', path: '/', label: 'Inicio', icon: LayoutDashboard },
  { type: 'leaf', path: '/l', label: 'Modo Lider', icon: HardHat, roles: [ROLES.LIDER, ROLES.ADMINISTRADOR] },
  { type: 'leaf', path: '/asignar-lideres', label: 'Asignar Lideres', icon: UserPlus, roles: [ROLES.ADMINISTRADOR, ROLES.SUPERVISOR, ROLES.ENCARGADO] },
  {
    type: 'section',
    id: 'programacion',
    label: 'Programacion',
    icon: Upload,
    children: [
      { type: 'leaf', path: '/excel-ingestion', label: 'Cargar Excel', icon: Upload },
      { type: 'leaf', path: '/excel-ingestion/npl', label: 'Cargar NPL', icon: FilePlus2 },
      { type: 'leaf', path: '/programacion/descargar', label: 'Descargar', icon: Download },
    ],
  },
  {
    type: 'section',
    id: 'plan-matriz',
    label: 'Plan Matriz',
    icon: Database,
    children: [
      { type: 'leaf', path: '/plan-matriz', label: 'Dashboard', icon: Database },
      { type: 'leaf', path: '/plan-matriz/mensual', label: 'Vista Mensual', icon: CalendarDays },
    ],
  },
  { type: 'leaf', path: '/pautas', label: 'Pautas', icon: ClipboardList },
  { type: 'leaf', path: '/actividades-previas', label: 'Actividades Previas', icon: ClipboardList, roles: [ROLES.ADMINISTRADOR, ROLES.SUPERVISOR, ROLES.OPERADOR, ROLES.LIDER] },
  {
    type: 'leaf',
    path: '/planes',
    label: 'Planes y Programas',
    icon: ListChecks,
    roles: [ROLES.ADMINISTRADOR, ROLES.SUPERVISOR, ROLES.ENCARGADO],
  },
  {
    type: 'leaf',
    path: '/mis-actividades',
    label: 'Mis actividades',
    icon: ListTodo,
    roles: [ROLES.OPERADOR, ROLES.LIDER, ROLES.ENCARGADO],
  },
  {
    type: 'section',
    id: 'gestion-ticket',
    label: 'Tickets',
    icon: FileText,
    children: [
      { type: 'leaf', path: '/tickets', label: 'Ejecucion de Ticket', icon: Wrench },
      { type: 'leaf', path: '/tickets/mantenedor', label: 'Mantenedor de Ticket', icon: FileText },
      {
        type: 'leaf',
        path: '/cierre',
        label: 'Cierre Mensual',
        icon: PackageCheck,
        roles: [ROLES.ADMINISTRADOR, ROLES.SUPERVISOR, ROLES.ENCARGADO],
      },
      { type: 'leaf', path: '/interferencias', label: 'Interferencias', icon: AlertTriangle },
    ],
  },
  {
    type: 'section',
    id: 'personal',
    label: 'Personal',
    icon: Users,
    children: [
      { type: 'leaf', path: '/organizacion', label: 'Organizacion', icon: Building2 },
      { type: 'leaf', path: '/usuarios', label: 'Usuarios', icon: UserCog, roles: [ROLES.ADMINISTRADOR, ROLES.SUPERVISOR] },
    ],
  },
  {
    type: 'section',
    id: 'reportes-kpi',
    label: 'Reportes',
    icon: BarChart3,
    children: [
      { type: 'leaf', path: '/reportes/tickets', label: 'Tickets', icon: FileBarChart },
      { type: 'leaf', path: '/reportes/tooltime', label: 'Tool Time', icon: Clock },
      { type: 'leaf', path: '/kpi', label: 'KPI', icon: BarChart3 },
    ],
  },
  {
    type: 'section',
    id: 'seguridad',
    label: 'Seguridad',
    icon: ShieldAlert,
    children: [
      { type: 'leaf', path: '/seguridad/peligros', label: 'Peligros', icon: AlertTriangle },
      { type: 'leaf', path: '/resso', label: 'RESSO', icon: FileWarning, roles: [ROLES.ADMINISTRADOR, ROLES.SUPERVISOR] },
      { type: 'leaf', path: '/charlas', label: 'Comunicacion', icon: MessageSquare, roles: [ROLES.ADMINISTRADOR, ROLES.SUPERVISOR] },
      { type: 'leaf', path: '/seguridad/documental', label: 'Documental', icon: FolderOpen },
    ],
  },
  {
    type: 'leaf',
    path: '/configuracion',
    label: 'Configuracion',
    icon: Settings,
    roles: [ROLES.ADMINISTRADOR],
  },
];

/* ─── Helpers ─── */

function isSectionActive(section: NavSectionItem, pathname: string): boolean {
  return section.children.some((child) => {
    if (child.path === '/') return pathname === '/';
    return pathname === child.path || pathname.startsWith(child.path + '/');
  });
}

// Visibility combines two independent axes:
//   1. role  — ¿este rol tiene permiso funcional? (`entry.roles`)
//   2. lock  — ¿el módulo está operativo para no-admins? (`isNavPathLocked`,
//              bloqueo TEMPORAL de la transición legacy → SIME nuevo)
// Una sección cae cuando se le vacían todos sus children por cualquiera de los
// dos motivos. Ver `@/constants/module-locks` para desbloquear un módulo.
function hasRole(roles: number[] | undefined, userRole: number | undefined): boolean {
  return !roles || (userRole !== undefined && roles.includes(userRole));
}

function filterByRole(entries: NavEntry[], userRole: number | undefined): NavEntry[] {
  return entries
    .filter((entry) => hasRole(entry.roles, userRole))
    .map((entry) => {
      if (entry.type === 'section') {
        const filteredChildren = entry.children.filter(
          (child) => hasRole(child.roles, userRole) && !isNavPathLocked(child.path, userRole),
        );
        if (filteredChildren.length === 0) return null;
        return { ...entry, children: filteredChildren };
      }
      if (isNavPathLocked(entry.path, userRole)) return null;
      return entry;
    })
    .filter(Boolean) as NavEntry[];
}

/* ─── Sidebar widths ─── */
const W_COLLAPSED = 'w-[68px]';
const W_EXPANDED = 'w-[260px]';

/* ─── Components ─── */

interface SidebarProps {
  mobile?: boolean;
  onClose?: () => void;
}

export function Sidebar({ mobile, onClose }: SidebarProps) {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const userRole = user?.tipoUsuarioId;
  const location = useLocation();
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [hovering, setHovering] = useState(false);

  const visibleEntries = filterByRole(navEntries, userRole);
  const isExpanded = mobile ? true : hovering;

  // Accordion: only one section open at a time (the active one by default)
  const activeSectionId = visibleEntries.find(
    (e): e is NavSectionItem => e.type === 'section' && isSectionActive(e, location.pathname),
  )?.id ?? null;
  const [openSectionId, setOpenSectionId] = useState<string | null>(activeSectionId);

  // Sync open section only when the route changes (not on every render)
  useEffect(() => {
    if (activeSectionId) {
      setOpenSectionId(activeSectionId);
    }
  }, [activeSectionId]);

  const toggleSection = (id: string) => {
    setOpenSectionId((prev) => (prev === id ? null : id));
  };

  const handleLogout = () => {
    setShowLogoutConfirm(false);
    logout();
    void navigate('/login', { replace: true });
  };

  const handleProfileClick = () => {
    if (mobile) onClose?.();
    void navigate('/perfil');
  };

  return (
    <nav
      role="navigation"
      aria-label={mobile ? 'Menu principal movil' : 'Menu principal'}
      onMouseEnter={mobile ? undefined : () => { setHovering(true); }}
      onMouseLeave={mobile ? undefined : () => { setHovering(false); }}
      className={cn(
        'h-screen bg-sime-sidebar-bg flex flex-col transition-all duration-300 ease-in-out overflow-hidden',
        'shadow-neu-side',
        mobile ? 'w-full' : isExpanded ? W_EXPANDED : W_COLLAPSED,
      )}
    >
      {/* Header */}
      <div className={cn(
        'flex items-center h-16 border-b border-sime-border-subtle/50 shrink-0',
        isExpanded ? 'px-4 justify-between' : 'justify-center',
      )}>
        {isExpanded ? (
          <span
            className="text-2xl font-extrabold whitespace-nowrap tracking-tight-lg"
            style={{ background: 'var(--sime-gradient-1)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
          >
            SIME
          </span>
        ) : (
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm font-extrabold"
            style={{ background: 'var(--sime-gradient-1)', boxShadow: '0 4px 12px var(--sime-accent-glow)' }}
          >
            S
          </div>
        )}
        {mobile ? (
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-sime-text-secondary hover:text-sime-text-primary transition-colors"
            aria-label="Cerrar menu"
          >
            <X className="h-5 w-5" />
          </button>
        ) : null}
      </div>

      {/* Nav items */}
      <ul className="flex-1 py-3 space-y-1 overflow-y-auto overflow-x-hidden px-2">
        {visibleEntries.map((entry) =>
          entry.type === 'leaf' ? (
            <NavLeaf
              key={entry.path + entry.label}
              item={entry}
              isExpanded={isExpanded}
              onNavigate={mobile ? onClose : undefined}
            />
          ) : (
            <NavSection
              key={entry.id}
              section={entry}
              isExpanded={isExpanded}
              pathname={location.pathname}
              open={openSectionId === entry.id}
              onToggle={() => { toggleSection(entry.id); }}
              onNavigate={mobile ? onClose : undefined}
            />
          ),
        )}
      </ul>

      {/* User section */}
      <div className="border-t border-sime-border-subtle/50 px-2 py-3 shrink-0">
        {isExpanded ? (
          <div className="flex items-center gap-3 px-1">
            <button
              onClick={handleProfileClick}
              className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm font-bold shadow-neu-xxs hover:opacity-90 transition-opacity"
              style={{ background: 'var(--sime-gradient-1)', boxShadow: '0 2px 8px var(--sime-accent-glow)' }}
              aria-label="Mi perfil"
            >
              {user?.nombre[0] ?? <User className="h-4 w-4" />}
            </button>
            <button
              onClick={handleProfileClick}
              className="flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
              aria-label="Mi perfil"
            >
              <p className="text-sm font-medium text-sime-text-primary truncate">
                {user?.nombre ?? 'Usuario'}
              </p>
              <p className="text-xs text-sime-text-tertiary truncate">
                {user?.email ?? ''}
              </p>
            </button>
            <button
              onClick={() => { setShowLogoutConfirm(true); }}
              className="shrink-0 p-2 rounded-xl text-sime-text-tertiary hover:text-sime-danger transition-colors"
              aria-label="Cerrar sesion"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={handleProfileClick}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold shadow-neu-xxs hover:opacity-90 transition-opacity"
              style={{ background: 'var(--sime-gradient-1)', boxShadow: '0 2px 8px var(--sime-accent-glow)' }}
              title={user?.nombre ?? 'Mi perfil'}
              aria-label="Mi perfil"
            >
              {user?.nombre[0] ?? <User className="h-4 w-4" />}
            </button>
            <button
              onClick={() => { setShowLogoutConfirm(true); }}
              className="p-2 rounded-xl text-sime-text-tertiary hover:text-sime-danger transition-colors shadow-neu-xxs"
              title="Cerrar sesion"
              aria-label="Cerrar sesion"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      <LogoutConfirmModal
        open={showLogoutConfirm}
        onClose={() => { setShowLogoutConfirm(false); }}
        onConfirm={handleLogout}
      />
    </nav>
  );
}

/* ─── NavLeaf ─── */

interface NavLeafProps {
  item: NavLeafItem;
  isExpanded: boolean;
  onNavigate?: () => void;
  nested?: boolean;
}

function NavLeaf({ item, isExpanded, onNavigate, nested }: NavLeafProps) {
  return (
    <li>
      <NavLink
        to={item.path}
        end
        onClick={onNavigate}
        title={isExpanded ? undefined : item.label}
        className={({ isActive }) =>
          cn(
            'flex items-center rounded-xl transition-all duration-150',
            isExpanded
              ? nested ? 'gap-3 px-3 py-2 ml-3' : 'gap-3 px-3 py-2.5'
              : 'justify-center py-2.5 px-0',
            'text-sime-text-secondary hover:text-sime-text-primary',
            isActive && [
              'text-sime-accent font-semibold',
              isExpanded
                ? 'bg-sime-accent/[0.08] border border-sime-accent/20 shadow-[inset_3px_0_0_var(--sime-accent)] rounded-none rounded-r-xl'
                : 'shadow-neu-inset-xs',
            ],
            !isActive && isExpanded && 'hover:bg-sime-surface-raised border border-transparent',
            !isActive && !isExpanded && 'hover:shadow-neu-xxs',
          )
        }
      >
        <item.icon className={cn('shrink-0', nested ? 'h-4 w-4' : isExpanded ? 'h-5 w-5' : 'h-6 w-6')} />
        {isExpanded ? (
          <span className={cn('font-medium whitespace-nowrap', nested ? 'text-xs' : 'text-sm')}>
            {item.label}
          </span>
        ) : null}
      </NavLink>
    </li>
  );
}

/* ─── NavSection (collapsible) ─── */

interface NavSectionProps {
  section: NavSectionItem;
  isExpanded: boolean;
  pathname: string;
  open: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
}

function NavSection({ section, isExpanded, pathname, open, onToggle, onNavigate }: NavSectionProps) {
  const active = isSectionActive(section, pathname);

  if (!isExpanded) {
    return (
      <li>
        <div
          className={cn(
            'flex items-center justify-center py-2.5 rounded-xl transition-all duration-150',
            active
              ? 'text-sime-accent shadow-neu-inset-xs'
              : 'text-sime-text-secondary hover:shadow-neu-xxs hover:text-sime-text-primary',
          )}
          title={section.label}
        >
          <section.icon className="h-6 w-6 shrink-0" />
        </div>
      </li>
    );
  }

  return (
    <li>
      <button
        onClick={onToggle}
        className={cn(
          'flex items-center gap-3 w-full px-3 py-2.5 rounded-xl transition-all duration-150',
          'text-sime-text-secondary hover:text-sime-text-primary',
          active && 'text-sime-accent',
          !active && 'hover:bg-sime-surface-raised',
        )}
        aria-expanded={open}
      >
        <section.icon className="h-5 w-5 shrink-0" />
        <span className="text-sm font-medium flex-1 text-left whitespace-nowrap">{section.label}</span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 transition-transform duration-200',
            open && 'rotate-180',
          )}
        />
      </button>

      {open ? (
        <ul className="mt-1 space-y-0.5">
          {section.children.map((child) => (
            <NavLeaf
              key={child.path + child.label}
              item={child}
              isExpanded
              onNavigate={onNavigate}
              nested
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}
